import React, { useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

// Worker servido de public/ (cópia de node_modules/pdfjs-dist/build/ —
// recopiar se atualizar o pacote): o bundle do CRA corrompe o worker do pdf.js
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

// Editor de retoques: mostra o PDF igualzinho e deixa escrever/cobrir por
// cima (texto e tarja branca). Tudo no navegador — o arquivo não sobe pro
// servidor; o salvar desenha os retoques no PDF original com o pdf-lib.

const LARGURA_ALVO = 820; // px de largura útil da página na tela

export default function EditorPdf({ onVoltar }) {
  const [arquivo, setArquivo] = useState(null);   // { nome, bytes }
  const [paginas, setPaginas] = useState([]);     // [{ alturaPt, larguraPt, escala }]
  const [elementos, setElementos] = useState([]); // [{ id, pagina, tipo, x, y, w, h, texto, tamanho }]
  const [ferramenta, setFerramenta] = useState('texto');
  const [selecionado, setSelecionado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const canvasRefs = useRef({});
  const drag = useRef(null); // { id, dx, dy } | { desenhando, pagina, x0, y0 } | { resize, id }

  async function abrirArquivo(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setCarregando(true);
    try {
      const bytes = await f.arrayBuffer();
      // cópia pro pdf-lib (o pdf.js transfere/consome o buffer dele)
      const doc = await pdfjs.getDocument({ data: bytes.slice(0) }).promise;
      const infos = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const escala = LARGURA_ALVO / base.width;
        infos.push({ larguraPt: base.width, alturaPt: base.height, escala });
      }
      setArquivo({ nome: f.name.replace(/\.pdf$/i, ''), bytes });
      setPaginas(infos);
      setElementos([]);
      setSelecionado(null);
      // renderiza depois que os canvases existirem no DOM
      setTimeout(async () => {
        for (let i = 1; i <= doc.numPages; i++) {
          const canvas = canvasRefs.current[i - 1];
          if (!canvas) continue;
          const page = await doc.getPage(i);
          const vp = page.getViewport({ scale: infos[i - 1].escala });
          const dpr = window.devicePixelRatio || 1;
          canvas.width = vp.width * dpr;
          canvas.height = vp.height * dpr;
          canvas.style.width = `${vp.width}px`;
          canvas.style.height = `${vp.height}px`;
          const ctx = canvas.getContext('2d');
          ctx.scale(dpr, dpr);
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
        }
      }, 50);
    } catch (err) {
      window.alert('Não consegui abrir esse PDF: ' + err.message);
    } finally {
      setCarregando(false);
      e.target.value = '';
    }
  }

  // ── Interação nas páginas ──────────────────────────────
  function posNaPagina(ev, pagina) {
    const el = document.getElementById(`pagina-pdf-${pagina}`);
    const r = el.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  }

  function cliqueNaPagina(ev, pagina) {
    if (drag.current) return; // soltou um arrasto em cima da página
    const { x, y } = posNaPagina(ev, pagina);
    if (ferramenta === 'texto') {
      const id = Date.now();
      setElementos(els => [...els, { id, pagina, tipo: 'texto', x, y, w: 240, texto: '', tamanho: 14 }]);
      setSelecionado(id);
    }
  }

  function iniciarTarja(ev, pagina) {
    if (ferramenta !== 'tarja') return;
    ev.preventDefault();
    const { x, y } = posNaPagina(ev, pagina);
    const id = Date.now();
    drag.current = { desenhando: id, pagina, x0: x, y0: y };
    setElementos(els => [...els, { id, pagina, tipo: 'tarja', x, y, w: 2, h: 2 }]);
    setSelecionado(id);
  }

  function moverMouse(ev) {
    const d = drag.current;
    if (!d) return;
    if (d.desenhando) {
      const { x, y } = posNaPagina(ev, d.pagina);
      setElementos(els => els.map(e => e.id !== d.desenhando ? e : {
        ...e,
        x: Math.min(d.x0, x), y: Math.min(d.y0, y),
        w: Math.max(2, Math.abs(x - d.x0)), h: Math.max(2, Math.abs(y - d.y0)),
      }));
    } else if (d.resize) {
      setElementos(els => els.map(e => e.id !== d.resize ? e : {
        ...e, w: Math.max(10, e.w + ev.movementX), h: Math.max(6, (e.h || 20) + ev.movementY),
      }));
    } else if (d.id) {
      setElementos(els => els.map(e => e.id !== d.id ? e : { ...e, x: e.x + ev.movementX, y: e.y + ev.movementY }));
    }
  }

  function soltarMouse() {
    // clique sem arrastar na ferramenta tarja deixaria um pontinho 2×2 — remove
    const d = drag.current;
    if (d?.desenhando) {
      setElementos(els => els.filter(e => e.id !== d.desenhando || e.w > 4 || e.h > 4));
    }
    setTimeout(() => { drag.current = null; }, 0);
  }

  const elSel = elementos.find(e => e.id === selecionado);

  // ── Salvar: desenha os retoques no PDF original ───────
  async function salvar() {
    if (!arquivo) return;
    setSalvando(true);
    try {
      const doc = await PDFDocument.load(arquivo.bytes);
      const fonte = await doc.embedFont(StandardFonts.Helvetica);
      const pgs = doc.getPages();
      for (const e of elementos) {
        const info = paginas[e.pagina];
        const pg = pgs[e.pagina];
        if (!info || !pg) continue;
        const s = info.escala;
        if (e.tipo === 'tarja') {
          pg.drawRectangle({
            x: e.x / s, y: info.alturaPt - (e.y + e.h) / s,
            width: e.w / s, height: e.h / s, color: rgb(1, 1, 1),
          });
        } else if (e.tipo === 'texto' && (e.texto || '').trim()) {
          const pt = e.tamanho / s;
          // Helvetica (WinAnsi) cobre o português; caracteres fora (emoji…) caem fora
          const limpo = (t) => t.split('').filter(ch => { try { fonte.encodeText(ch); return true; } catch { return false; } }).join('');
          e.texto.split('\n').forEach((linha, i) => {
            pg.drawText(limpo(linha), {
              x: e.x / s + 2 / s,
              y: info.alturaPt - (e.y + 3) / s - pt * 0.9 - i * pt * 1.25,
              size: pt, font: fonte, color: rgb(0, 0, 0),
            });
          });
        }
      }
      const salvo = await doc.save();
      const url = URL.createObjectURL(new Blob([salvo], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${arquivo.nome} (editado).pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert('Erro ao salvar: ' + err.message);
    } finally {
      setSalvando(false);
    }
  }

  const btnFerr = (f, rotulo, dica) => (
    <button onClick={() => setFerramenta(f)} title={dica}
      style={{ background: ferramenta === f ? '#0a1628' : '#fff', color: ferramenta === f ? '#fff' : '#0a1628', border: '1px solid #0a1628', borderRadius: 5, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
      {rotulo}
    </button>
  );

  return (
    <div style={{ maxWidth: 900 }} onMouseMove={moverMouse} onMouseUp={soltarMouse}>
      <button onClick={onVoltar} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer', padding: 0 }}>← Gerador de Arquivos</button>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, margin: '4px 0 10px' }}>
        <div>
          <h1 style={{ fontSize: 17, fontWeight: 700, color: '#0a1628', margin: 0 }}>✏️ Editor de PDF</h1>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>Escreva ou cubra por cima do documento e salve um PDF idêntico ao original com os retoques. Nada sai do seu computador.</p>
        </div>
        <label style={{ background: '#0a1628', color: '#fff', borderRadius: 5, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          📂 {arquivo ? 'Abrir outro PDF' : 'Abrir PDF'}
          <input type="file" accept="application/pdf" onChange={abrirArquivo} style={{ display: 'none' }} />
        </label>
      </div>

      {carregando && <div style={{ padding: 30, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Abrindo o documento…</div>}

      {arquivo && !carregando && (
        <>
          <div style={{ position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#fff', border: '1px solid #c8d0da', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
            {btnFerr('texto', 'T Texto', 'Clique no documento onde quer escrever')}
            {btnFerr('tarja', '⬜ Tarja branca', 'Clique e arraste sobre o trecho que quer cobrir')}
            {elSel?.tipo === 'texto' && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}>
                · Fonte:
                <button onClick={() => setElementos(els => els.map(e => e.id === selecionado ? { ...e, tamanho: Math.max(8, e.tamanho - 1) } : e))} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', padding: '1px 7px' }}>−</button>
                <strong>{elSel.tamanho}</strong>
                <button onClick={() => setElementos(els => els.map(e => e.id === selecionado ? { ...e, tamanho: Math.min(48, e.tamanho + 1) } : e))} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', padding: '1px 7px' }}>+</button>
              </span>
            )}
            {elSel && (
              <button onClick={() => { setElementos(els => els.filter(e => e.id !== selecionado)); setSelecionado(null); }}
                style={{ background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 5, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✕ Remover selecionado</button>
            )}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{elementos.length} retoque{elementos.length === 1 ? '' : 's'}</span>
            <button onClick={salvar} disabled={salvando || !elementos.length}
              style={{ background: elementos.length ? '#16a34a' : '#9ca3af', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: elementos.length ? 'pointer' : 'default' }}>
              {salvando ? 'Salvando…' : '💾 Salvar PDF'}
            </button>
          </div>

          {paginas.map((p, idx) => (
            <div key={idx} id={`pagina-pdf-${idx}`}
              onMouseDown={ev => { if (ev.target.closest('[data-retoque]')) return; iniciarTarja(ev, idx); }}
              onClick={ev => { if (ev.target.closest('[data-retoque]')) return; cliqueNaPagina(ev, idx); }}
              style={{ position: 'relative', width: LARGURA_ALVO, margin: '0 auto 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.18)', cursor: ferramenta === 'tarja' ? 'crosshair' : 'text', background: '#fff' }}>
              <canvas ref={el => { canvasRefs.current[idx] = el; }} style={{ display: 'block' }} />
              {elementos.filter(e => e.pagina === idx).map(e => (
                <div key={e.id} data-retoque
                  onMouseDown={ev => { ev.stopPropagation(); setSelecionado(e.id); if (ev.target.dataset.alca) { drag.current = { resize: e.id }; } else if (ev.target.tagName !== 'TEXTAREA') { drag.current = { id: e.id }; } ev.preventDefault(); }}
                  onClick={ev => ev.stopPropagation()}
                  style={{
                    position: 'absolute', left: e.x, top: e.y,
                    width: e.w, height: e.tipo === 'tarja' ? e.h : undefined,
                    background: e.tipo === 'tarja' ? '#fff' : 'transparent',
                    outline: selecionado === e.id ? '2px dashed #0369a1' : (e.tipo === 'tarja' ? '1px dashed #d1d5db' : 'none'),
                    cursor: 'move', zIndex: 10,
                  }}>
                  {e.tipo === 'texto' && (
                    <textarea autoFocus={!e.texto} value={e.texto}
                      onChange={ev => setElementos(els => els.map(x => x.id === e.id ? { ...x, texto: ev.target.value } : x))}
                      onMouseDown={ev => { ev.stopPropagation(); setSelecionado(e.id); }}
                      placeholder="digite aqui…" rows={Math.max(1, (e.texto.match(/\n/g) || []).length + 1)}
                      style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', resize: 'none', overflow: 'hidden', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: e.tamanho, lineHeight: 1.25, color: '#000', padding: '3px 2px 0', cursor: 'text' }} />
                  )}
                  {selecionado === e.id && e.tipo === 'tarja' && (
                    <div data-alca title="Arraste para redimensionar"
                      style={{ position: 'absolute', right: -5, bottom: -5, width: 10, height: 10, background: '#0369a1', borderRadius: 2, cursor: 'nwse-resize' }} />
                  )}
                </div>
              ))}
            </div>
          ))}
          <p style={{ fontSize: 11.5, color: '#9ca3af', textAlign: 'center', marginBottom: 20 }}>
            T Texto: clique onde quer escrever · ⬜ Tarja: arraste sobre o que quer cobrir · arraste os retoques pra posicionar · 💾 baixa "{arquivo.nome} (editado).pdf"
          </p>
        </>
      )}

      {!arquivo && !carregando && (
        <div style={{ border: '2px dashed #c8d0da', borderRadius: 8, padding: 50, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
          Abra um PDF pra começar — contratos recebidos, procurações pra completar, documentos pra corrigir…
        </div>
      )}
    </div>
  );
}
